// Parsing pasted spreadsheet text and CSV files into customer rows.
//
// Kept separate from the dialog so the awkward parts — quoted CSV fields,
// header detection, column order — can be reasoned about and tested without a
// browser.

export interface ParsedRow {
  name: string;
  taxId?: string;
  phone: string;
  address?: string;
  /** 1-based line number in the source, so the preview can point at the file. */
  line: number;
}

export interface ParseResult {
  rows: ParsedRow[];
  /** Lines that could not be read at all, with why. */
  problems: { line: number; text: string; reason: string }[];
  headerSkipped: boolean;
}

/**
 * Split one line into fields.
 *
 * Excel puts tabs between cells when you copy a range, so a tab wins whenever
 * one is present — a comma inside an address ("Jl. Merdeka No. 5, Jakarta")
 * would otherwise split that address in two. Only when there is no tab do we
 * treat the line as CSV, and then quotes have to be honoured for the same
 * reason.
 */
export function splitLine(line: string): string[] {
  if (line.includes('\t')) return line.split('\t').map((c) => c.trim());

  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i += 1; }  // "" is a literal quote
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',' || ch === ';') {
      out.push(cur.trim()); cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const HEADER_WORDS = /nama|name|nik|npwp|telepon|telpon|hp|phone|alamat|address|sku|qty|jumlah|lot|batch|expired|expiry|kadaluarsa|kedaluwarsa|satuan|unit|form|order/i;

/** A first line that names the columns rather than holding data. */
function looksLikeHeader(cells: string[]): boolean {
  const named = cells.filter((c) => HEADER_WORDS.test(c)).length;
  // Two or more recognisable labels, and no cell that is obviously a phone
  // number — a real row for someone called "Nama Suryani" must not be eaten.
  return named >= 2 && !cells.some((c) => /\d{6,}/.test(c));
}

/**
 * Column order is fixed: Nama, NIK/NPWP, Nomor Telepon, Alamat.
 *
 * Not detected from the header, on purpose. Header text varies wildly
 * ("No HP", "Telp.", "WA") and guessing wrong silently files phone numbers
 * under address. A fixed order is something the operator can see and verify in
 * the preview before committing.
 */
export function parseCustomerRows(text: string): ParseResult {
  const res: ParseResult = { rows: [], problems: [], headerSkipped: false };
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  lines.forEach((raw, idx) => {
    const line = idx + 1;
    if (!raw.trim()) return;

    const cells = splitLine(raw);
    if (idx === 0 && looksLikeHeader(cells)) { res.headerSkipped = true; return; }

    const [name = '', taxId = '', phone = '', address = ''] = cells;
    if (!name.trim() && !phone.trim()) {
      res.problems.push({ line, text: raw.trim().slice(0, 80), reason: 'Baris kosong / tidak terbaca' });
      return;
    }
    res.rows.push({
      name: name.trim(),
      taxId: taxId.trim() || undefined,
      phone: phone.trim(),
      address: address.trim() || undefined,
      line,
    });
  });

  return res;
}

// ── Stock allocations ────────────────────────────────────────────────────────

export interface ParsedAllocationRow {
  sku: string;
  qty: number;
  /** Raw text as typed, so an unparseable qty can be shown back to the operator. */
  qtyRaw: string;
  lotNumber?: string;
  /** ISO yyyy-mm-dd. Undefined when the cell was blank. */
  expiryDate?: string;
  /**
   * True when the cell had text that could not be read as a date.
   *
   * Distinguished from blank on purpose. Treating an unreadable date as "no
   * expiry" is the quiet failure here: batch identity includes the expiry, so a
   * mistyped date would silently create a second, parallel batch of the same
   * lot instead of updating the one that exists. The importer refuses the row.
   */
  expiryUnreadable?: boolean;
  unit?: string;
  orderForm?: string;
  line: number;
}

export interface ParseAllocationResult {
  rows: ParsedAllocationRow[];
  problems: { line: number; text: string; reason: string }[];
  headerSkipped: boolean;
}

/**
 * Dates arrive from spreadsheets in whatever the operator's locale produced.
 * Preferred and handed out by the template is a NAMED month — 12-Nov-2030 —
 * because it is the one shape nobody can misread. Also accepted: ISO
 * (2030-11-12) and day-first numeric (12/11/2030). A bare 12/31/2027 is
 * refused: 03/04/2027 would be March or April depending on who typed it, and
 * an expiry guessed wrong is printed on a customer's invoice.
 */
/**
 * Month names, English and Indonesian, abbreviated or spelled out. A named
 * month is the only spreadsheet date shape that carries no ambiguity at all,
 * which is why it is now what the template hands out.
 */
const MONTHS: Record<string, number> = {
  jan: 1, januari: 1, january: 1,
  feb: 2, februari: 2, february: 2, peb: 2,
  mar: 3, maret: 3, march: 3,
  apr: 4, april: 4,
  may: 5, mei: 5,
  jun: 6, juni: 6, june: 6,
  jul: 7, juli: 7, july: 7,
  aug: 8, agu: 8, agt: 8, agustus: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, okt: 10, october: 10, oktober: 10,
  nov: 11, november: 11, nopember: 11,
  dec: 12, des: 12, december: 12, desember: 12,
};

/** "30" → 2030. Expiries are in the future, so a bare year is this century. */
const fullYear = (y: string) => (y.length === 4 ? y : String(2000 + Number(y)));

export function parseSheetDate(raw: string): string | undefined {
  const t = (raw ?? '').trim();
  if (!t) return undefined;

  const iso = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const [, y, m, d] = iso;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // 12-Nov-30, 01 Mar 2030, 30/Sep/2030. The preferred shape: a named month
  // cannot be read as a day, so there is nothing left to guess.
  const named = t.match(/^(\d{1,2})[\s/.-]+([A-Za-z]+)[\s/.-]+(\d{2}|\d{4})$/);
  if (named) {
    const [, d, monthWord, y] = named;
    const m = MONTHS[monthWord.toLowerCase()];
    if (!m || Number(d) < 1 || Number(d) > 31) return undefined;
    return `${fullYear(y)}-${String(m).padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // d/m/y or d-m-y, still accepted for sheets that already use it. Rejected
  // when the first number cannot be a day, rather than silently swapping to
  // m/d/y — a guessed expiry is printed on a customer's invoice.
  const dmy = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    if (Number(d) > 31 || Number(m) > 12) return undefined;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return undefined;
}

/**
 * Column order: SKU, Qty, Lot number, Expired date, Satuan, Form order.
 *
 * Keyed by SKU rather than product name. A name is retyped, abbreviated and
 * misspelled in every spreadsheet that passes through a warehouse; the SKU is
 * the thing that is actually copied. Rows whose SKU matches nothing are shown
 * as errors instead of silently creating stock against the wrong product.
 */
export function parseAllocationRows(text: string): ParseAllocationResult {
  const res: ParseAllocationResult = { rows: [], problems: [], headerSkipped: false };
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  lines.forEach((raw, idx) => {
    const line = idx + 1;
    if (!raw.trim()) return;

    const cells = splitLine(raw);
    // The Excel template carries a product-name formula in column G on every
    // row, so an unused row still reaches here as ",,,,,,". Only the six
    // columns this importer reads decide whether a row was filled in.
    if (cells.slice(0, 6).every((c) => !c.trim())) return;
    if (idx === 0) {
      const named = cells.filter((c) => HEADER_WORDS.test(c)).length;
      if (named >= 2 && !cells.some((c) => /^\d+$/.test(c.trim()))) {
        res.headerSkipped = true;
        return;
      }
    }

    const [sku = '', qtyRaw = '', lotNumber = '', expiry = '', unit = '', orderForm = ''] = cells;
    if (!sku.trim()) {
      res.problems.push({ line, text: raw.trim().slice(0, 80), reason: 'SKU kosong' });
      return;
    }

    // Accepts "1.200" and "1,200" — thousands separators are what a spreadsheet
    // hands over, and treating them as decimals would import 1 unit of 1200.
    const cleaned = qtyRaw.replace(/[.,\s]/g, '');
    const qty = Number(cleaned);

    const expiryRaw = expiry.trim();
    const expiryDate = parseSheetDate(expiryRaw);

    res.rows.push({
      sku: sku.trim(),
      qty: Number.isFinite(qty) ? qty : NaN,
      qtyRaw: qtyRaw.trim(),
      lotNumber: lotNumber.trim() || undefined,
      expiryDate,
      expiryUnreadable: !!expiryRaw && !expiryDate,
      unit: unit.trim() || undefined,
      orderForm: orderForm.trim() || undefined,
      line,
    });
  });

  return res;
}
